import { useState, useEffect } from 'react';
import { submitFeedback, captureScreenshot, uploadFeedbackScreenshot } from '../../services/feedbackService';
import FeedbackList from './FeedbackList';

const FEEDBACK_TYPES = [
  {
    id: 'bug',
    label: 'Bug',
    icon: 'bug_report',
    helperText: 'Noko fungerer ikkje som forventa',
  },
  {
    id: 'forslag',
    label: 'Forslag',
    icon: 'lightbulb',
    helperText: 'Del ideen din med oss',
  },
  {
    id: 'sporsmal',
    label: 'Spørsmål',
    icon: 'help',
    helperText: 'Kva lurer du på?',
  },
];

export default function FeedbackModal({ onClose, initialScreenshotBlob }) {
  const [viewMode, setViewMode] = useState('form'); // 'form' | 'list' | 'success'
  const [feedbackType, setFeedbackType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [issueUrl, setIssueUrl] = useState(null);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setViewMode('form');
    onClose();
  };

  const selectedType = FEEDBACK_TYPES.find((t) => t.id === feedbackType);

  // Use pre-captured screenshot from FeedbackButton (captured before modal opened)
  useEffect(() => {
    if (initialScreenshotBlob) {
      setScreenshotBlob(initialScreenshotBlob);
      const url = URL.createObjectURL(initialScreenshotBlob);
      setScreenshotPreview(url);
    }
  }, [initialScreenshotBlob]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  const handleCaptureScreenshot = async () => {
    setIsCapturing(true);

    // Get the modal container to hide it temporarily during capture
    const modalContainer = document.querySelector('[data-feedback-modal]');

    try {
      // Hide modal before capture
      if (modalContainer) {
        modalContainer.style.visibility = 'hidden';
      }

      // Small delay to ensure DOM updates
      await new Promise(resolve => setTimeout(resolve, 50));

      const blob = await captureScreenshot();
      if (blob) {
        setScreenshotBlob(blob);
        const url = URL.createObjectURL(blob);
        setScreenshotPreview(url);
      }
    } catch (err) {
      console.error('Screenshot capture failed:', err);
    } finally {
      // Show modal again
      if (modalContainer) {
        modalContainer.style.visibility = 'visible';
      }
      setIsCapturing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Tittel er påkravd');
      return;
    }

    if (feedbackType === 'bug' && !description.trim()) {
      setError('Skildring er påkravd for bug-rapportar');
      return;
    }

    setIsSubmitting(true);

    try {
      let screenshotUrl = null;

      if (includeScreenshot && screenshotBlob) {
        screenshotUrl = await uploadFeedbackScreenshot(screenshotBlob);
      }

      const feedbackData = {
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        screenshotUrl,
        pageUrl: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      const result = await submitFeedback(feedbackData);

      if (result.success) {
        setSubmitStatus('success');
        setViewMode('success');
        setIssueUrl(result.issueUrl);
      } else {
        throw new Error(result.error || 'Kunne ikkje sende tilbakemelding');
      }
    } catch (err) {
      setError(err.message);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- SUCCESS VIEW ---
  if (viewMode === 'success') {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background-dark/95 backdrop-blur-md p-4 font-display">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-4xl text-green-600">check_circle</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Takk for tilbakemeldinga!</h2>
          <p className="text-gray-600 mb-6">Vi har motteke meldinga di og vil sjå på ho snart.</p>
          {issueUrl && (
            <a
              href={issueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:underline mb-4"
            >
              <span className="material-symbols-outlined text-lg">open_in_new</span>
              Sjå issue på GitHub
            </a>
          )}
          <button
            onClick={() => setViewMode('list')}
            className="w-full py-3 rounded-xl border-2 border-feedback text-feedback font-bold hover:bg-feedback/5 transition-colors mb-3"
          >
            <span className="inline-flex items-center gap-2">
              <span className="material-symbols-outlined text-lg">list</span>
              Sjå alle tilbakemeldingar
            </span>
          </button>
          <button
            onClick={handleClose}
            className="w-full py-3 rounded-xl bg-feedback text-white font-bold hover:bg-feedback-hover transition-colors"
          >
            Lukk
          </button>
        </div>
      </div>
    );
  }

  // --- LIST VIEW ---
  if (viewMode === 'list') {
    return (
      <div data-feedback-modal className="fixed inset-0 z-[60] flex items-center justify-center bg-background-dark/95 backdrop-blur-md p-4 font-display">
        <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
          <FeedbackList onBack={() => setViewMode(submitStatus === 'success' ? 'success' : 'form')} />
        </div>
      </div>
    );
  }

  // --- FORM VIEW (default) ---
  return (
    <div data-feedback-modal className="fixed inset-0 z-[60] flex items-center justify-center bg-background-dark/95 backdrop-blur-md p-4 font-display">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Gi tilbakemelding</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setViewMode('list')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Sjå alle tilbakemeldingar"
                title="Sjå alle tilbakemeldingar"
              >
                <span className="material-symbols-outlined text-gray-500">list</span>
              </button>
              <button
                onClick={handleClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Lukk"
              >
                <span className="material-symbols-outlined text-gray-500">close</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Tilbakemeldingar blir sendt til utviklingsteamet via GitHub.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Type</label>
            <div className="grid grid-cols-3 gap-3">
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFeedbackType(type.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                    feedbackType === type.id
                      ? 'bg-eq-primary/10 text-eq-primary shadow-ambient'
                      : 'ghost-border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl">{type.icon}</span>
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-3">{selectedType?.helperText}</p>
          </div>

          <div>
            <label htmlFor="feedback-title" className="block text-sm font-medium text-gray-700 mb-2">
              Tittel
            </label>
            <input
              id="feedback-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Kort oppsummering..."
              maxLength={100}
              className="w-full px-4 py-3 rounded-xl ghost-border ghost-border-focus outline-none transition-all"
            />
          </div>

          <div>
            <label htmlFor="feedback-description" className="block text-sm font-medium text-gray-700 mb-2">
              Skildring
            </label>
            <textarea
              id="feedback-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Fortel oss meir..."
              rows={4}
              className="w-full px-4 py-3 rounded-xl ghost-border ghost-border-focus outline-none transition-all resize-none"
            />
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeScreenshot}
                onChange={(e) => {
                  setIncludeScreenshot(e.target.checked);
                  if (!e.target.checked) {
                    setScreenshotPreview(null);
                    setScreenshotBlob(null);
                  }
                }}
                className="w-5 h-5 rounded border-gray-300 text-feedback focus:ring-feedback"
              />
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span className="material-symbols-outlined text-lg">photo_camera</span>
                Legg ved skjermbilete
              </span>
            </label>

            {includeScreenshot && (
              <div className="mt-3 rounded-xl ghost-border overflow-hidden">
                {isCapturing ? (
                  <div className="h-32 flex items-center justify-center bg-gray-50">
                    <span className="text-sm text-gray-500">Tar skjermbilete...</span>
                  </div>
                ) : screenshotPreview ? (
                  <img
                    src={screenshotPreview}
                    alt="Skjermbilete førehandsvising"
                    className="w-full h-auto max-h-48 object-contain bg-gray-50"
                  />
                ) : (
                  <div className="h-32 flex items-center justify-center bg-gray-50">
                    <button
                      type="button"
                      onClick={handleCaptureScreenshot}
                      className="text-sm text-feedback hover:underline"
                    >
                      Ta skjermbilete på nytt
                    </button>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-2">
              Automatisk lagt til: Side, tidspunkt
            </p>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-3 rounded-xl ghost-border text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-3 rounded-xl bg-feedback text-white font-bold hover:bg-feedback-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Sender...' : 'Send tilbakemelding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
